import { alpha, createTheme, darken, getContrastRatio, lighten } from '@mui/material/styles';

const DEFAULT_PRIMARY_COLOR = '#217CEE';
const DEFAULT_COLOR_MODE = 'light';

const getPrimaryTokens = (primaryColor = DEFAULT_PRIMARY_COLOR) => {
  const normalizedColor = /^#([0-9A-F]{3}){1,2}$/i.test(primaryColor)
    ? primaryColor
    : DEFAULT_PRIMARY_COLOR;

  return {
    main: normalizedColor,
    light: lighten(normalizedColor, 0.5),
    dark: darken(normalizedColor, 0.16),
    contrastText: getContrastRatio(normalizedColor, '#ffffff') >= 1.45 ? '#ffffff' : '#111111',
    danger: '#d32f2f',
    border: alpha(normalizedColor, 0.24),
    surface: alpha(normalizedColor, 0.08),
    surfaceStrong: alpha(normalizedColor, 0.14),
    tint: lighten(normalizedColor, 0.38),
  };
};

const createPrimaryPalette = (primaryColor = DEFAULT_PRIMARY_COLOR) => {
  const {
    main,
    light,
    dark,
    contrastText,
    danger,
  } = getPrimaryTokens(primaryColor);

  return {
    main,
    light,
    dark,
    contrastText,
    danger,
  };
};

const getSurfaceTokens = (mode = DEFAULT_COLOR_MODE) => {
  if (mode === 'dark') {
    return {
      background: '#0f1720',
      paper: '#17212b',
      paperElevated: '#1d2935',
      textPrimary: '#f8fafc',
      textSecondary: '#cbd5e1',
      textMuted: '#94a3b8',
      border: '#2b3642',
      hoverSurface: '#223040',
    };
  }

  return {
    background: '#fafafa',
    paper: '#ffffff',
    paperElevated: '#ffffff',
    textPrimary: '#333333',
    textSecondary: '#414651',
    textMuted: '#A4A7AE',
    border: '#E9EAEB',
    hoverSurface: '#f5f7fa',
  };
};

export const getThemeCssVariables = (primaryColor = DEFAULT_PRIMARY_COLOR, mode = DEFAULT_COLOR_MODE) => {
  const tokens = getPrimaryTokens(primaryColor);
  const surfaces = getSurfaceTokens(mode);

  return {
    '--color-main': tokens.main,
    '--color-main-border': tokens.border,
    '--color-main-light': tokens.surface,
    '--color-main-light-strong': tokens.surfaceStrong,
    '--color-primary-tint': tokens.tint,
    '--color-primary-dark': tokens.dark,
    '--color-bg': surfaces.background,
    '--color-surface': surfaces.paper,
    '--color-surface-elevated': surfaces.paperElevated,
    '--color-text-primary': surfaces.textPrimary,
    '--color-text-secondary': surfaces.textSecondary,
    '--color-text-muted': surfaces.textMuted,
    '--color-border': surfaces.border,
    '--color-hover-surface': surfaces.hoverSurface,
  };
};

export const createAppTheme = (
  primaryColor = DEFAULT_PRIMARY_COLOR,
  mode = DEFAULT_COLOR_MODE,
) => {
  const surfaces = getSurfaceTokens(mode);

  return createTheme({
  palette: {
    mode,
    primary: createPrimaryPalette(primaryColor),
    secondary: {
      main: surfaces.textMuted,
      light: mode === 'dark' ? '#243140' : '#f2f2f2',
      dark: surfaces.textSecondary,
      contrastText: surfaces.textPrimary,
      danger: '#d32f2f',
    },
    background: {
      default: surfaces.background,
      paper: surfaces.paper,
    },
    text: {
      primary: surfaces.textPrimary,
      secondary: surfaces.textSecondary,
    },
    divider: surfaces.border,
    orange: {
      main: '#ee9321',
      light: '#ee9321',
      dark: '#e2820a',
      contrastText: '#ffffff',
      danger: '#d32f2f',
    },
    rubyDust: {
      main: '#e11157',
      light: '#E42867',
      dark: '#CA0F4E',
      contrastText: '#ffffff',
      danger: '#d32f2f',
    },
    shamrock: {
      main: '#33CC99',
      light: '#5CD6AD',
      dark: '#29A37A',
      contrastText: '#ffffff',
      danger: '#d32f2f',
    },
    purple: {
      main: '#7266FA',
      light: '#7266FA',
      dark: '#7266FA',
      contrastText: '#ffffff',
      danger: '#d32f2f',
    },
    crimson: {
      main: '#BD1435',
      light: '#BD1435',
      dark: '#BD1435',
      contrastText: '#ffffff',
      danger: '#d32f2f',
    },
    matortho: {
      main: '#AA2415',
      light: '#AA2415',
      dark: '#AA2415',
      contrastText: '#ffffff',
      danger: '#d32f2f',
    },
  },
  typography: {
    fontFamily: `"Source Sans Pro", "Roboto", "Helvetica", "Arial", sans-serif`,
    button: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 20,
  },
  overrides: {
    MuiButton: {
      root: {
        padding: '6px 48px',
        boxShadow: 'none !important',
        '&:hover': {
          boxShadow:
            '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important',
        },
        '&:active': {
          boxShadow:
            '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important',
        },
        '&:focus': {
          boxShadow:
            '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12) !important',
        },
      },
    },
    MuiFormGroup: {
      root: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '& > *': { flex: 1 },
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: mode === 'dark' ? 'rgba(15,23,32,0.72)' : 'rgba(0,0,0,0.35)',
      },
    },
    MuiBadge: {
      root: {
        borderRadius: '50%',
        width: '35%',
      },
    },
    MuiFormControlLabel: {
      label: {
        color: '#b1b1b1',
        fontSize: '0.9rem',
        marginLeft: 4,
      },
    },
    MuiTypography: {
      body1: {
        fontFamily: 'Source Sans Pro, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
        lineHeight: 1.5,
        height: '100%',
      },
    },
    MuiPaper: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: 8,
      },
    },
    MuiTab: {
      wrapper: {
        alignItems: 'flex-start',
        textTransform: 'capitalize',
        paddingLeft: 8,
      },
    },
  },
  });
};

const defaultTheme = createAppTheme();

export { DEFAULT_PRIMARY_COLOR, DEFAULT_COLOR_MODE, defaultTheme };
export default defaultTheme;
